import { useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";
import { CONTENT } from "../data/content";
import { scrollToSection } from "../lib/smooth";

const lineReveal = {
    hidden: { y: "115%" },
    visible: (i) => ({
        y: 0,
        transition: { duration: 1.1, delay: 0.35 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
    }),
};

const reelEmbed = (id, muted) =>
    `https://www.youtube-nocookie.com/embed/${id}?playsinline=1&rel=0${
        muted ? "&autoplay=1&mute=1&controls=0&loop=1&playlist=" + id + "&modestbranding=1&disablekb=1&fs=0" : ""
    }`;

export const Hero = () => {
    const [reelOpen, setReelOpen] = useState(false);
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const textOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section id="top" ref={ref} className="relative h-screen overflow-hidden" data-testid="hero-section">
            <motion.div style={{ y: videoY }} className="absolute inset-0">
                <motion.img
                    src={CONTENT.hero.poster}
                    alt=""
                    aria-hidden="true"
                    className="h-full w-full object-cover"
                    style={{ filter: "grayscale(0.25) contrast(1.08) brightness(0.92) blur(8px)" }}
                    animate={{ scale: [1.1, 1.22] }}
                    transition={{ duration: 28, repeat: Infinity, repeatType: "reverse", ease: "linear" }}
                    data-testid="hero-bg-video"
                />
                <div className="absolute inset-0 bg-black/70" />
            </motion.div>

            <motion.div
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-x-0 top-0 z-20 h-[7vh] origin-top bg-[#050505]"
            />
            <motion.div
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-x-0 bottom-0 z-20 h-[7vh] origin-bottom bg-[#050505]"
            />

            <motion.div
                style={{ opacity: textOpacity }}
                className="relative z-10 flex h-full flex-col justify-end px-6 pb-[14vh] md:px-12"
            >
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.4 }}
                    className="mb-4 font-body text-xs uppercase tracking-[0.4em] text-zinc-300"
                    data-testid="hero-role"
                >
                    {CONTENT.role} — {CONTENT.location}
                </motion.p>
                <h1 className="font-display leading-[0.85] tracking-tight text-white">
                    {CONTENT.nameLines.map((line, i) => (
                        <span key={line} className="block overflow-hidden">
                            <motion.span
                                custom={i}
                                variants={lineReveal}
                                initial="hidden"
                                animate="visible"
                                className="block text-[22vw] md:text-[17vw]"
                            >
                                {line}
                            </motion.span>
                        </span>
                    ))}
                </h1>
                <div className="mt-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 1.2 }}
                        className="max-w-md font-editorial text-lg italic text-zinc-300 md:text-xl"
                        data-testid="hero-tagline"
                    >
                        {CONTENT.hero.tagline}
                    </motion.p>
                    <motion.button
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 1.5, ease: [0.16, 1, 0.3, 1] }}
                        whileHover={{ scale: 1.06 }}
                        whileTap={{ scale: 0.96 }}
                        onClick={() => setReelOpen(true)}
                        data-testid="play-reel-button"
                        className="group flex w-fit items-center gap-4 border border-white/30 px-7 py-4 transition-colors duration-500 hover:border-[#E50914] hover:bg-[#E50914]"
                    >
                        <Play className="h-4 w-4 fill-current" />
                        <span className="font-body text-xs uppercase tracking-[0.35em]">Play Reel</span>
                    </motion.button>
                </div>
            </motion.div>

            <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.2, duration: 1 }}
                onClick={() => scrollToSection("#work")}
                data-testid="hero-scroll-hint"
                className="absolute bottom-[9vh] left-1/2 z-10 hidden -translate-x-1/2 font-body text-[10px] uppercase tracking-[0.5em] text-zinc-400 transition-colors duration-300 hover:text-white md:block"
            >
                Scroll
            </motion.button>

            <AnimatePresence>
                {reelOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="fixed inset-0 z-[80] flex items-center justify-center bg-black/95 p-4 md:p-16"
                        data-testid="reel-overlay"
                    >
                        <button
                            onClick={() => setReelOpen(false)}
                            data-testid="reel-close-button"
                            aria-label="Close reel"
                            className="absolute right-6 top-6 flex h-12 w-12 items-center justify-center border border-white/30 text-white transition-colors duration-300 hover:border-[#E50914] hover:bg-[#E50914]"
                        >
                            <X className="h-5 w-5" />
                        </button>
                        <motion.div
                            initial={{ scale: 0.92, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                            className="aspect-video w-full max-w-6xl"
                        >
                            <iframe
                                className="h-full w-full"
                                src={reelEmbed(CONTENT.hero.reelId, false)}
                                title={`${CONTENT.name} — showreel`}
                                allow="autoplay; fullscreen; picture-in-picture"
                                allowFullScreen
                                referrerPolicy="strict-origin-when-cross-origin"
                                data-testid="reel-video"
                            />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};
